import {
  Box,
  Button,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {IMediaTrack} from '../../../../dto/MediaTrack.dto';
import IconAddSong from '../../../../asset/icons/icon_addsong.svg';
import WalkupMusicSelectionTable from '../table/WalkupMusicSelectionTable';
import IconWarning from '../../../../asset/icons/icon_warning.svg';
import {productLevelSelector} from '../../../../store/Slices/companySlice';
import {ConditionalRender} from '../../../../components/UserCan';
import CustomModal from '../../../../components/modal/CustomModal';
import CreateClipMusicSelection from '../../../music/on-demand-mode/create-clip/CreateClipMusicSelection';
import {StationProvider} from '../../../../components/models/stations/StationContext';
import UploadSongDialog from '../dialogs/UploadSongDialog';
import {INewWalkupPlayer, IWalkupLibraryTypeProp} from './WalkupLineupStepper';
import {SONG_MAX_DURATION, useSongSession} from '../dialogs/SongSessionContext';

export type AllowedPlayerTypeKeys = Extract<
  keyof INewWalkupPlayer,
  'hitter' | 'pitcher' | 'athlete'
>;

const SongMappingCmpt = React.memo(
  ({
    isAthlete,
    players,
    setPlayers,
    isPreviousDataDiabled,
    selectedPlayerIdAndType,
    walkupTypeSelected,
    isSaveProgress,
  }: {
    isAthlete: boolean;
    players: INewWalkupPlayer[];
    setPlayers: React.Dispatch<React.SetStateAction<INewWalkupPlayer[]>>;
    isPreviousDataDiabled: boolean;
    selectedPlayerIdAndType: {
      id: number;
      type: AllowedPlayerTypeKeys;
      isSongRequestRejected: boolean;
    } | null;
    walkupTypeSelected: IWalkupLibraryTypeProp;
    isSaveProgress: boolean;
  }) => {
    const productLevel = useSelector(productLevelSelector);
    const isLevel0 = productLevel.includes('Level 0 (ODO Only)');
    const {decrementSongCount} = useSongSession();
    const [playerId, setPlayerId] = useState<number>(0);
    const [isUploadDialog, setIsUploadDialog] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [tabType, setTabType] = useState('');
    const [selectedPlayerType, setSelectedPlayerType] =
      useState<AllowedPlayerTypeKeys>('athlete');

    const onSelect = useCallback(
      (track: IMediaTrack) => {
        const currentPlayerType = players.find(o => o.id === playerId)?.[selectedPlayerType];
        if (currentPlayerType?.file && (currentPlayerType.fileDuration ?? 0) > SONG_MAX_DURATION) {
          decrementSongCount();
        }
        setPlayers(prev => {
          if (playerId) {
            return prev.map(o => {
              if (o.id === playerId) {
                return {
                  ...o,
                  [selectedPlayerType]: {
                    ...o[selectedPlayerType],
                    file: null,
                    fileDuration: undefined,
                    media_track_id: track.id,
                    track,
                  },
                };
              }
              return o;
            });
          }
          return prev;
        });
      },
      [playerId, selectedPlayerType, setPlayers, decrementSongCount, players],
    );

    const onDeSelect = useCallback(
      (_track: IMediaTrack) => {
        // For Both type (B), prevent deselection only for previously saved songs
        if (walkupTypeSelected === 'B') {
          const player = players.find(o => o.id === playerId);
          if (player?.[selectedPlayerType]?.walkup_track_id) {
            return;
          }
        }

        setPlayers(prev => {
          if (playerId) {
            return prev.map(o => {
              if (o.id === playerId) {
                delete o.track;
                return {
                  ...o,
                  [selectedPlayerType]: {
                    ...o[selectedPlayerType],
                    file: null,
                    media_track_id: null,
                    track_artist: '',
                    track_name: '',
                  },
                };
              }
              return o;
            });
          }
          return prev;
        });
      },
      [playerId, selectedPlayerType, setPlayers, walkupTypeSelected, players],
    );

    const track = useMemo(() => {
      const track = players.find(o => o.id === playerId)?.[
        selectedPlayerType as AllowedPlayerTypeKeys
      ]?.track;
      if (track) {
        return [track];
      }
      return [];
    }, [playerId, players, selectedPlayerType]);

    const handleSongUpload = useCallback(
      (artist: string, track_name: string, file: File | null, fileDuration?: number) => {
        const previousPlayerType = players.find(o => o.id === playerId)?.[selectedPlayerType];
        const previousWasLong = (previousPlayerType?.fileDuration ?? 0) > SONG_MAX_DURATION;
        const newIsLong = (fileDuration ?? 0) > SONG_MAX_DURATION;
        if (previousWasLong && !newIsLong) {
          decrementSongCount();
        }
        setPlayers(prev =>
          prev.map(o => {
            if (o.id === playerId) {
              const type = o.type === 'new' ? 'new' : 'change';
              const previousSelectedPlayerType = o[selectedPlayerType];
              delete previousSelectedPlayerType?.track;
              return {
                ...o,
                [selectedPlayerType]: {
                  ...previousSelectedPlayerType,
                  type,
                  file,
                  fileDuration,
                  track_artist: artist,
                  track_name: track_name,
                  media_track_id: null,
                },
              };
            }
            return o;
          }),
        );
        setIsUploadDialog(false);
      },
      [playerId, selectedPlayerType, setPlayers, decrementSongCount, players],
    );

    useEffect(() => {
      if (
        selectedPlayerIdAndType &&
        selectedPlayerIdAndType.id &&
        selectedPlayerIdAndType.type
      ) {
        setSelectedPlayerType(selectedPlayerIdAndType.type);
        setPlayerId(selectedPlayerIdAndType.id);
      }
    }, [selectedPlayerIdAndType]);

    if (isUploadDialog && playerId) {
      return (
        <UploadSongDialog
          handleSave={handleSongUpload}
          close={() => setIsUploadDialog(false)}
          existingFileDuration={players.find(o => o.id === playerId)?.[selectedPlayerType]?.fileDuration}
        />
      );
    }

    const handleCloseViewModal = () => {
      setViewModalOpen(false);
    };

    const handleOpenViewModal = () => {
      setViewModalOpen(true);
    };

    return (
      <Grid container>
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            background: `var(--blue_variant36)`,
            height: {md: 'calc(100vh - 280px)'},
            overflow: {md: 'visible', xs: 'auto'},
          }}>
          <Stack direction='column' spacing={2} sx={{height: '65vh', gap: 1}}>
            <Box className='playerBox'>
              {isAthlete ? (
                <Stack spacing={2} sx={{height: '90%', gap: 1}}>
                  <ListPlayerTypeBased
                    selectedPlayerType={selectedPlayerType}
                    setSelectedPlayerType={setSelectedPlayerType}
                    playerId={playerId}
                    setPlayerId={setPlayerId}
                    players={players}
                    playerType='athlete'
                    isPreviousDataDiabled={isPreviousDataDiabled}
                    walkupTypeSelected={walkupTypeSelected}
                    isSaveProgress={isSaveProgress}
                  />
                </Stack>
              ) : (
                <Stack spacing={2} sx={{height: '98%', gap: 1}}>
                  <ListPlayerTypeBased
                    selectedPlayerType={selectedPlayerType}
                    setSelectedPlayerType={setSelectedPlayerType}
                    playerId={playerId}
                    setPlayerId={setPlayerId}
                    players={players}
                    playerType='hitter'
                    isPreviousDataDiabled={isPreviousDataDiabled}
                    walkupTypeSelected={walkupTypeSelected}
                    isSaveProgress={isSaveProgress}
                  />
                  <ListPlayerTypeBased
                    selectedPlayerType={selectedPlayerType}
                    setSelectedPlayerType={setSelectedPlayerType}
                    playerId={playerId}
                    setPlayerId={setPlayerId}
                    players={players}
                    playerType='pitcher'
                    isPreviousDataDiabled={isPreviousDataDiabled}
                    walkupTypeSelected={walkupTypeSelected}
                    isSaveProgress={isSaveProgress}
                  />
                </Stack>
              )}
              {tabType !== 'clip' ? (
                <ConditionalRender condition={!isLevel0}>
                  <Button
                    variant='contained'
                    disabled={!playerId}
                    onClick={() => setIsUploadDialog(true)}
                    className='flex px-3 mx-auto'>
                    <img
                      src={IconAddSong}
                      alt='New Song Request Icon'
                      className='mr-4'
                    />
                    Upload a Song
                  </Button>
                </ConditionalRender>
              ) : (
                <ConditionalRender condition={!isLevel0}>
                  <Button
                    variant='contained'
                    size='small'
                    disabled={!playerId}
                    className='flex px-3 mx-auto'
                    onClick={handleOpenViewModal}>
                    Create Custom Clip
                  </Button>
                </ConditionalRender>
              )}
              <StationProvider>
                <CustomModal
                  open={viewModalOpen}
                  onClose={handleCloseViewModal}
                  title='Create Custom Clip'
                  body={
                    <CreateClipMusicSelection
                      clipType={'customClip'}
                      walkupType={'walkupClip'}
                      handleCloseViewModal={handleCloseViewModal}
                    />
                  }
                  actionButtonText='Close Modal'
                  onActionButtonClick={handleCloseViewModal}
                  maxwidth='2000'
                  customHeight='95vh'
                />
              </StationProvider>
            </Box>
          </Stack>
        </Grid>
        <Grid item xs={12} md={9} className='px-3'>
          <WalkupMusicSelectionTable
            selectedTracks={track as IMediaTrack[]}
            onSelect={onSelect}
            onDeselect={onDeSelect}
            setTabType={setTabType}
          />
        </Grid>
      </Grid>
    );
  },
);

export default SongMappingCmpt;

export const ListPlayerTypeBased = ({
  selectedPlayerType,
  setSelectedPlayerType,
  players,
  playerId,
  setPlayerId,
  playerType,
  isStudentForm = false,
  isPreviousDataDiabled = false,
  walkupTypeSelected,
  isSaveProgress = false,
}: {
  selectedPlayerType: AllowedPlayerTypeKeys;
  setSelectedPlayerType: (val: AllowedPlayerTypeKeys) => void;
  players: INewWalkupPlayer[];
  playerId: number;
  setPlayerId: React.Dispatch<React.SetStateAction<number>>;
  playerType: AllowedPlayerTypeKeys;
  isStudentForm?: boolean;
  isPreviousDataDiabled?: boolean;
  walkupTypeSelected?: IWalkupLibraryTypeProp;
  isSaveProgress?: boolean;
}) => {
  const isAthelete = playerType === 'athlete';

  const handleSelect = useCallback(
    (id: number) => {
      setPlayerId(id);
      setSelectedPlayerType(playerType);
    },
    [playerType, setPlayerId, setSelectedPlayerType],
  );
  const totalPlayers = players.filter(
    player =>
      player.player_type?.some(
        type => type.toLowerCase() === playerType.toLowerCase(),
      ),
  ).length;
  const activePlayers = players.filter(
    player => player[playerType]?.walkup_track_id,
  ).length;
  return (
    <>
      <Box
        sx={{
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
        }}>
        <Typography className='text-center mt-1 font-bold'>
          {`${playerType.toUpperCase()} (${activePlayers} / ${totalPlayers})`}
        </Typography>
      </Box>
      <Box
        className={`${
          isAthelete ? (totalPlayers > 10 ? 'scrollbar' : '') : 'scrollbar'
        }`}
        sx={{
          height: isAthelete
            ? theme => theme.spacing(65)
            : theme => theme.spacing(25),
          overflowY: 'scroll',
          '& .list-items-sec': {
            maxHeight: isAthelete
              ? theme => theme.spacing(73)
              : theme => theme.spacing(20),
          },
        }}>
        <List className={`list-items-sec`}>
          {players
            .filter(
              val =>
                val.player_type
                  ?.map(v => v.toLowerCase())
                  .includes(playerType.toLowerCase()),
            )
            .map(o => {
              const isDisabled = isPreviousDataDiabled && o.id > 0;
              const hasTrack = Boolean(o[playerType]?.walkup_track_id);
              const isWBType = ['W', 'B'].includes(walkupTypeSelected ?? '');
              const both = walkupTypeSelected === 'B';
              const hasPendingTrackRequest =
                isStudentForm &&
                o.id !== playerId &&
                !o[playerType]?.walkup_track_id &&
                !o[playerType]?.track &&
                Boolean(o[playerType]?.track_name) &&
                Boolean(o[playerType]?.track_artist);
              return (
                <ListItem
                  disablePadding
                  key={o.id}
                  secondaryAction={
                    isStudentForm &&
                    Boolean(o[playerType]?.walkup_track_id) ? null : !(
                        (o[playerType]?.track?.track_artist &&
                          o[playerType]?.track?.track_name) ||
                        (o[playerType]?.track_artist &&
                          o[playerType]?.track_name)
                      ) ? (
                      <img
                        src={IconWarning}
                        alt='New Song Request Icon'
                        className='w-26 h-32 pt-1'
                      />
                    ) : null
                  }>
                  <ListItemButton
                    onClick={() => handleSelect(o.id)}
                    disabled={
                      !isSaveProgress &&
                      // If W or B type without previous track, enable player (override all conditions)
                      (hasPendingTrackRequest ||
                        ((!isWBType || hasTrack) &&
                          ((hasTrack && (isStudentForm || both)) ||
                            (isDisabled &&
                              !o[playerType]?.song_request_rejected))))
                    }
                    selected={
                      o.id === playerId && selectedPlayerType === playerType
                    }>
                    <ListItemText
                      className='ellipsis'
                      primary={`#${o.player_no} ${o.first_name} ${o.last_name}`}
                      secondary={
                        o[playerType]?.track?.track_artist ||
                        o[playerType]?.track_artist
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
        </List>
      </Box>
    </>
  );
};
